import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  ngHelper,
  IFormattedOutletSchedule,
  OUTLET_SCHEDULE_TYPES,
  OUTLET_FORMATTED_SCHEDULE_TYPES,
} from '@cakeiteasy/web-utils';
import type * as MapLibre from 'maplibre-gl';

import { OUTLETS_MAP_CONTEXT } from '../../constants/common.constants';
import {
  configureMapLibreWorker,
  createBounds,
  hasCoordinates,
  latLng,
  simplifyMapLibreOutletStyle,
} from '../../helpers/maplibre.helper';
import { DEFAULT_MAPLIBRE_CONFIG } from '../../constants/maplibre.constants';

export interface IOutletModel {
  id: number;
  address?: string;
  cityName?: string;
  name?: string;
  postNumber?: string;
  latitude?: number;
  longitude?: number;
  bakery?: { id: number; name: string };
  expand?: boolean;
  formattedWorkingDays?: IFormattedOutletSchedule[];
  formattedPickupSchedule?: IFormattedOutletSchedule[];
}

@Component({
  standalone: false,
  selector: 'ui-outlets-map',
  templateUrl: './ui-outlets-map.component.html',
  styleUrls: ['./ui-outlets-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UiOutletsMapComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() outlets: IOutletModel[];
  @Input() mapContext: string = OUTLETS_MAP_CONTEXT.FOR_BAKERY;
  @Input() blockWheelScrolling: boolean;
  @Input() scheduleType: string = OUTLET_SCHEDULE_TYPES.WORKING_DAYS;

  @ViewChild('mapContainer', { static: true }) mapContainer: ElementRef<HTMLDivElement>;

  public expandedOutlet: IOutletModel = null;
  public scheduleKey: string = OUTLET_FORMATTED_SCHEDULE_TYPES[OUTLET_SCHEDULE_TYPES.WORKING_DAYS];
  private _maplibre: typeof MapLibre;
  private _map: MapLibre.Map;
  private _markers = new Map<number, MapLibre.Marker>();
  private _isViewInitialized = false;
  private _isMapInitializing = false;
  private _isDestroyed = false;

  constructor(@Inject(PLATFORM_ID) private _platformId: Object) {}

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'outlets', (outlets: IOutletModel[]) => {
      if (!outlets) {
        return;
      }

      if (outlets.length === 1) {
        this.expandedOutlet = outlets[0];
      }

      if (!this._map) {
        void this._tryInitMap();
        return;
      }

      this._fitBounds(outlets);
      this._renderMarkers();
    });

    ngHelper.ifChanged(changes, 'scheduleType', (scheduleType: string) => {
      if (!scheduleType) {
        return;
      }
      this.scheduleKey = OUTLET_FORMATTED_SCHEDULE_TYPES[scheduleType];
      this._renderMarkers();
    });

    ngHelper.ifChanged(changes, 'blockWheelScrolling', () => {
      this._syncScrollWheel();
    });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this._platformId)) {
      return;
    }

    this._isViewInitialized = true;
    await this._tryInitMap();
  }

  public openOutlet(outlet: IOutletModel) {
    if (this.expandedOutlet?.id === outlet.id) {
      this.expandedOutlet = null;
    } else {
      this.expandedOutlet = outlet;
    }

    this._renderMarkers();
  }

  ngOnDestroy(): void {
    this._isDestroyed = true;
    this._clearMarkers();
    this._map?.remove();
  }

  private _renderMarkers() {
    if (!this._map || !this._maplibre || !this.outlets) {
      return;
    }

    this._clearMarkers();

    this.outlets.filter(hasCoordinates).forEach((outlet) => {
      const marker = this._createMarker(outlet);
      marker.addTo(this._map);
      this._markers.set(outlet.id, marker);
    });
  }

  private _clearMarkers() {
    this._markers.forEach((marker) => marker.remove());
    this._markers.clear();
  }

  private async _tryInitMap(): Promise<void> {
    if (
      this._isDestroyed ||
      !this._isViewInitialized ||
      this._map ||
      this._isMapInitializing ||
      !isPlatformBrowser(this._platformId)
    ) {
      return;
    }

    const initialView = this._getInitialView();

    if (!initialView) {
      return;
    }

    try {
      this._isMapInitializing = true;
      this._maplibre = await import('maplibre-gl');

      if (this._isDestroyed) {
        return;
      }

      configureMapLibreWorker(this._maplibre);
      const { styleUrl, maxZoom } = DEFAULT_MAPLIBRE_CONFIG;
      this._map = new this._maplibre.Map({
        container: this.mapContainer.nativeElement,
        style: styleUrl,
        maxZoom,
        scrollZoom: !this.blockWheelScrolling,
        attributionControl: { compact: true },
        ...initialView,
      });
      simplifyMapLibreOutletStyle(this._map);

      this._map.addControl(new this._maplibre.NavigationControl({ showCompass: false }), 'bottom-left');
      this._renderMarkers();

      if ('center' in initialView) {
        setTimeout(() => {
          if (this._map && !this._isDestroyed) {
            this._map.panBy([0, -60], { duration: 800 });
          }
        }, 50);
      }
    } finally {
      this._isMapInitializing = false;
    }
  }

  private _getInitialView(): Partial<MapLibre.MapOptions> {
    const positionedOutlets = this.outlets?.filter(hasCoordinates) || [];

    if (positionedOutlets.length === 1) {
      return {
        center: latLng(positionedOutlets[0] as { latitude: number; longitude: number }),
        zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
      };
    }

    if (positionedOutlets.length > 1) {
      return {
        bounds: this._getBoundsLike(positionedOutlets),
        fitBoundsOptions: {
          maxZoom: 16,
          duration: 0,
        },
      };
    }

    return null;
  }

  private _getBoundsLike(outlets: IOutletModel[]): [[number, number], [number, number]] {
    let minLat = Number.POSITIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    outlets.forEach((outlet) => {
      minLat = Math.min(minLat, outlet.latitude);
      minLng = Math.min(minLng, outlet.longitude);
      maxLat = Math.max(maxLat, outlet.latitude);
      maxLng = Math.max(maxLng, outlet.longitude);
    });

    return [[minLng - 0.1, minLat], [maxLng + 0.1, maxLat]];
  }

  private _createMarker(outlet: IOutletModel): MapLibre.Marker {
    const expanded = this.expandedOutlet?.id === outlet.id;
    const element = document.createElement('div');
    element.innerHTML = this._getMarkerHtml(outlet, expanded);
    element.style.zIndex = expanded ? '1000' : '0';
    element.addEventListener('click', () => this.openOutlet(outlet));

    return new this._maplibre.Marker({
      element,
      anchor: 'top-left',
    }).setLngLat(latLng(outlet as { latitude: number; longitude: number }));
  }

  private _fitBounds(outlets: IOutletModel[]) {
    if (!this._map || !this._maplibre) {
      return;
    }

    const positionedOutlets = outlets.filter(hasCoordinates);

    if (!positionedOutlets.length) {
      return;
    }

    if (positionedOutlets.length === 1) {
      const outlet = positionedOutlets[0];
      setTimeout(() => {
        this._map.jumpTo({
          center: latLng(outlet as { latitude: number; longitude: number }),
          zoom: DEFAULT_MAPLIBRE_CONFIG.initialZoom,
        });
        this._map.panBy([0, -60], { duration: 800 });
      }, 50);
      return;
    }

    const bounds = createBounds(this._maplibre, positionedOutlets);

    let minLng = 10000,
      maxLng = -10000;
    positionedOutlets.forEach((o) => {
      minLng = Math.min(minLng, o.longitude);
      maxLng = Math.max(maxLng, o.longitude);
    });

    const centerLat = bounds.getCenter().lat;
    bounds.extend([minLng - 0.1, centerLat]);
    bounds.extend([maxLng + 0.1, centerLat]);

    setTimeout(() => {
      this._map.fitBounds(bounds, { maxZoom: 16, duration: 800 });
    }, 50);
  }

  private _syncScrollWheel(): void {
    if (!this._map) {
      return;
    }

    if (this.blockWheelScrolling) {
      this._map.scrollZoom.disable();
    } else {
      this._map.scrollZoom.enable();
    }
  }

  private _getMarkerHtml(outlet: IOutletModel, expanded: boolean): string {
    const title = this.mapContext === OUTLETS_MAP_CONTEXT.FOR_CITY ? outlet.bakery?.name : outlet.name;

    const content = expanded
      ? `
      <div class="snazzy-content font-sm">
        <div class="fw-bold">
          ${this.mapContext === OUTLETS_MAP_CONTEXT.FOR_CITY ? `<div class="mb-1 nowrap">${outlet.name}</div>` : ''}
          <div>${outlet.address}</div>
          <div class="mt-1">${outlet.postNumber} ${outlet.cityName}</div>
        </div>
        <div class="mt-3">
          ${(outlet[this.scheduleKey] || [])
            .filter((days) => !days.isClosed)
            .map((days) => `<div>${days.days}: ${days.begin} - ${days.end}</div>`)
            .join('')}
        </div>
      </div>`
      : '';

    return `
      <div class="outlet-snazzy-info-wrap ${expanded ? 'expanded' : ''}">
        <div class="outlet-snazzy-info ${expanded ? 'expand text-left' : ''}">
          <div class="fw-bold font-sm snazzy-title">
            <div>${title}</div>
          </div>
          ${content}
        </div>
      </div>`;
  }
}
